import { createContext, useContext, useState } from 'react';
import { en } from '../locales/en.js';
import { ko } from '../locales/ko.js';
import { ja } from '../locales/ja.js';
import { fr } from '../locales/fr.js';
import { es } from '../locales/es.js';
import { it } from '../locales/it.js';
import { de } from '../locales/de.js';
import { ru } from '../locales/ru.js';
import { pt } from '../locales/pt.js';
import { tr } from '../locales/tr.js';
import { el } from '../locales/el.js';

// 순서 = 언어 선택 목록 순서. GA4 사용자 수 기준으로 en 뒤에 많은 쪽부터 두고,
// ko/ja 는 홈마켓이라 뒤라도 유지한다 (2026-09 기준 사용자 수는 가장 적다).
const LOCALES = { en, fr, es, pt, de, it, ru, tr, el, ja, ko };
const SUPPORTED = Object.keys(LOCALES);
const STORAGE_KEY = 'voda_locale';
// rename 모달 placeholder 개수 — 로케일 배열에서 파생한다. 로케일에 문구를
// 더하거나 빼면 여기 안 고쳐도 따라온다. t('renamePlaceholders.3') 처럼
// 인덱스를 경로로 넣으면 로케일 누락 시 영어로 폴백되는 것도 그대로 쓴다.
export const RENAME_PLACEHOLDER_COUNT = LOCALES.en.renamePlaceholders.length;

export function randomRenamePlaceholderIndex() {
  return Math.floor(Math.random() * RENAME_PLACEHOLDER_COUNT);
}


// 지금 앱 언어 — 서버에 알려 줄 때 (initializeUser → 푸시 문구 언어).
export function currentLang() {
  return detectLang();
}

function detectLang() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && SUPPORTED.includes(saved)) return saved;
  const browser = navigator.language?.split('-')[0];
  return SUPPORTED.includes(browser) ? browser : 'en';
}

// 언어 하나에 묶인 t / styleName. Provider 가 쓰고, 언어가 주소로 정해지는 화면
// (랜딩 /ko, /ja … 와 그 정적 사본을 굽는 scripts/prerender-landing.mjs) 도 쓴다.
export function makeTranslator(lang) {
  // t('key') or t('styles.modern.name') or t('photoLabel', { n: 2 })
  const t = (key, params) => {
    const parts = key.split('.');
    let val = LOCALES[lang];
    for (const p of parts) val = val?.[p];

    // Fallback to English
    if (val === undefined || val === null) {
      val = LOCALES.en;
      for (const p of parts) val = val?.[p];
    }

    if (typeof val !== 'string') return key;
    if (!params) return val;
    return val.replace(/\{(\w+)\}/g, (_, k) => (params[k] ?? `{${k}}`));
  };

  // 스타일 이름 전용 해석기.
  //
  // 왜 필요한가: 스타일 카탈로그(`services/ai-service.js`)의 `name` 은 AI
  // 프롬프트에 실어 보내는 영어 원본이다. 화면에 그걸 그대로 쓰면 한국어 /
  // 일본어에서도 "Modern", "Cleanup" 처럼 영어가 나온다 — 실제로 스타일 선택
  // 그리드, 필터 칩, 디자인 카드가 전부 그 상태였고 번역은 로케일 파일에
  // 이미 다 있었는데 아무도 읽지 않았다.
  //
  // spaceCategory 가 실제 데이터와 안 맞는 경우가 있어 (ai-service 의
  // styleNameFor 도 같은 이유로 폴백을 돈다) 나머지 카탈로그도 순서대로 찾는다.
  const styleName = (key, spaceCategory = 'interior') => {
    if (!key) return '';
    const primary = spaceCategory === 'exterior' ? 'exteriorStyles'
      : spaceCategory === 'garden' ? 'gardenStyles'
      : 'styles';
    for (const path of [primary, 'styles', 'exteriorStyles', 'gardenStyles']) {
      const k = `${path}.${key}.name`;
      const v = t(k);
      if (v !== k) return v;   // t() 는 못 찾으면 키를 그대로 돌려준다
    }
    return key;
  };
  return { t, styleName };
}

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  const setLang = (l) => {
    if (!SUPPORTED.includes(l)) return;
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
    // 푸시 문구를 바뀐 언어로 받도록 서버 쪽 users/{uid}.lang 도 갱신.
    import('../firebase.js').then(({ auth, db }) => {
      const u = auth.currentUser;
      if (!u || u.isAnonymous) return;
      import('firebase/firestore').then(({ doc, setDoc }) =>
        setDoc(doc(db, 'users', u.uid), { lang: l }, { merge: true }).catch(() => {}));
    });
  };

  const { t, styleName } = makeTranslator(lang);

  // Direct access to the current locale object (for arrays like onboarding steps)
  const locale = LOCALES[lang];

  return (
    <LocaleContext.Provider value={{ t, lang, setLang, locale, styleName }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale() {
  return useContext(LocaleContext);
}

export const SUPPORTED_LANGS = SUPPORTED;
export const LANG_LABELS = { en: 'English', fr: 'Français', es: 'Español', pt: 'Português', de: 'Deutsch', it: 'Italiano', ru: 'Русский', tr: 'Türkçe', el: 'Ελληνικά', ja: '日本語', ko: '한국어' };
